import { Link } from "react-router-dom";

/**
 * Chuyển cây menu (lấy từ API) sang định dạng `items` của AntD Menu.
 * Tách khỏi layout shell để tái sử dụng & dễ đọc.
 *
 * CHỈ mục cấp 1 mới có icon. Mục con bỏ icon vì icon ở đó không mang thêm
 * thông tin — cả nhóm dùng chung một hình, chỉ tổ làm cột menu chật lại và đẩy
 * nhãn dài phải cắt bớt ("Chương trình tích đi..."). Cấp bậc đã được biểu thị
 * bằng thụt lề rồi.
 *
 * Phần thụt lề bù cho chỗ icon trống nằm ở `index.css` (selector
 * `.cms-sidebar-menu`) chứ không đặt ở đây: viết `style` tại item thì lúc
 * sidebar thu gọn, menu con hiện trong popup cũng dính luôn.
 *
 * @param depth 0 = cấp gốc. Chỉ dùng để quyết định có gắn icon hay không.
 */
export function renderMenuItems(items, user, depth = 0) {
  if (!user) return [];
  if (!(items?.length > 0)) return [];

  return items.map((item) => {
    const base = { key: String(item.id) };
    if (depth === 0) base.icon = <i className={item.icon} />;

    if (item.children?.length > 0) {
      return {
        ...base,
        label: item.name,
        children: renderMenuItems(item.children, user, depth + 1),
      };
    }

    return { ...base, label: <Link to={item.url}>{item.name}</Link> };
  });
}

/**
 * Suy ra mục menu ứng với URL đang mở.
 *
 * Cần thiết vì `Menu` không tự biết: click thì AntD tự nhớ item vừa bấm, nhưng
 * F5 hoặc vào bằng link trực tiếp là mất sạch highlight — nhìn cột menu không
 * biết mình đang đứng ở đâu.
 *
 * Khớp tuyệt đối trước; không có thì lấy mục có URL là tiền tố DÀI NHẤT của
 * đường dẫn, để màn chi tiết (`/customer/123`) vẫn sáng ở mục `/customer`.
 * Riêng `/` chỉ nhận khớp tuyệt đối, vì nó là tiền tố của mọi đường dẫn.
 *
 * @returns {{ selectedKeys: string[], parentKeys: string[] }} `parentKeys` là
 *   chuỗi nhóm cha (gốc → trong), dùng để mở sẵn nhóm chứa trang hiện tại.
 */
export function findActiveKeys(items, pathname) {
  let best = null; // { keys, urlLength, exact }

  const walk = (nodes, ancestors) => {
    if (!(nodes?.length > 0)) return;

    nodes.forEach((node) => {
      const key = String(node.id);

      if (node.children?.length > 0) {
        walk(node.children, [...ancestors, key]);
        return;
      }
      if (!node.url) return;

      const exact = pathname === node.url;
      const isPrefix = node.url !== "/" && pathname.startsWith(`${node.url}/`);
      if (!exact && !isPrefix) return;

      const better =
        !best ||
        (exact && !best.exact) ||
        (exact === best.exact && node.url.length > best.urlLength);
      if (better) {
        best = { keys: [key, ...ancestors], urlLength: node.url.length, exact };
      }
    });
  };

  walk(items, []);
  if (!best) return { selectedKeys: [], parentKeys: [] };

  const [selected, ...parentKeys] = best.keys;
  return { selectedKeys: [selected], parentKeys };
}
